import { Chess } from 'chess.js';

/**
 * Game Logic Service
 * Single Responsibility: Chess game logic and move validation
 * Open/Closed: Can be extended with new game modes without modification
 */
export class GameLogic {
  static validateMove(game, sourceSquare, targetSquare) {
    // Check if game is in valid state
    if (!game || game.isGameOver()) {
      return { success: false, error: 'Game is over or invalid' };
    }

    // Check if squares are valid
    if (!sourceSquare || !targetSquare) {
      return { success: false, error: 'Invalid squares' };
    }

    // Check if there's a piece on the source square
    const piece = game.get(sourceSquare);
    if (!piece) {
      return { success: false, error: 'No piece on source square' };
    }
    
    const gameCopy = new Chess(game.fen());
    let move = null;
    
    try {
      
      // Try normal move
      move = gameCopy.move({ from: sourceSquare, to: targetSquare });
      
      // If failed and it's a pawn move to promotion rank, try with queen promotion
      if (!move) {
        const piece = gameCopy.get(sourceSquare);
        if (piece?.type === 'p' && (targetSquare[1] === '8' || targetSquare[1] === '1')) {
          move = gameCopy.move({ from: sourceSquare, to: targetSquare, promotion: 'q' });
        }
      }
      
      return { success: !!move, move, game: gameCopy };
    } catch (error) {
      return { success: false, error, game: gameCopy };
    }
  }

  static getPossibleMoves(game, square) {
    const moves = game.moves({ square, verbose: true });
    return moves.map(move => move.to);
  }

  static isPieceMovableByPlayer(piece, playerColor) {
    if (!piece || !playerColor) return false;
    return (playerColor === 'white' && piece.color === 'w') ||
           (playerColor === 'black' && piece.color === 'b');
  }

  static isCorrectMove(actualMove, expectedMove) {
    return actualMove === expectedMove;
  }

  static getMoveType(move) {
    const moveSAN = move?.san;
    if (moveSAN === 'O-O' || moveSAN === 'O-O-O') {
      return 'castle';
    } else if (moveSAN?.includes('+')) {
      return 'check';
    } else if (move?.captured) {
      return 'capture';
    } else {
      return 'normal';
    }
  }

  static reconstructGameToMove(moves, targetMoveIndex) {
    const game = new Chess();
    for (let i = 0; i < targetMoveIndex; i++) {
      try {
        game.move(moves[i]);
      } catch (error) {
        console.error('Error reconstructing game:', error);
        return null;
      }
    }
    return game;
  }
}

/**
 * Move Result Factory
 * Single Responsibility: Creating standardized move results
 */
export class MoveResult {
  constructor(success, type, data = {}) {
    this.success = success;
    this.type = type;
    this.data = data;
  }

  static success(type, data) {
    return new MoveResult(true, type, data);
  }

  static failure(type, data) {
    return new MoveResult(false, type, data);
  }

  static invalidMove(message = 'Invalid chess move! Try again.') {
    return MoveResult.failure('invalid', { message: '' });
  }

  static wrongTurn(message = 'Wait for your turn!') {
    return MoveResult.failure('wrong_turn', { message: '' });
  }

  static wrongMove(expected, actual) {
    return MoveResult.failure('wrong_move', { 
      message: `Wrong move! Expected: ${expected}, You played: ${actual}`,
      expected,
      actual
    });
  }

  static correctMove(move, game, newMoveIndex) {
    return MoveResult.success('correct_move', { 
      message: '',
      move,
      game,
      newMoveIndex
    });
  }

  static lineComplete() {
    return MoveResult.success('line_complete', {
      message: 'Line completed!'
    });
  }

  static freePlay(game) {
    return MoveResult.success('free_play', {
      message: 'Opening complete! Free play mode.',
      game
    });
  }
}